import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import GoogleReviewsBadge from "./GoogleReviewsBadge";

const HERO_IMG = "/images/hero-home.png";
const BATTERY_IMG = "https://static.prod-images.emergentagent.com/jobs/044cc6b7-ce9e-4305-861a-d9ffda9c2795/images/165f387264e19f09a946f2e21e610b717f1d5ea762b1e1349f62851866f25b61.png";

const Hero = () => {
  const { t } = useLang();
  return (
    <section className="relative pt-32 lg:pt-40 pb-20 lg:pb-28 overflow-hidden">
      <div className="blob-pink absolute -top-20 -right-20 z-0" />
      <div className="blob-pink absolute top-1/2 -left-32 z-0 opacity-50" />

      <div className="relative max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-5 z-10"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-brand-soft border border-brand/20 mb-7" data-testid="hero-eyebrow">
            <span className="w-2 h-2 rounded-full bg-brand animate-pulse" />
            <span className="text-xs font-semibold uppercase tracking-widest text-brand-deep">{t.hero.eyebrow}</span>
          </div>

          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter text-navy-900 leading-[1.02] text-balance" data-testid="hero-title">
            {t.hero.title.split(" ").slice(0, -3).join(" ")}{" "}
            <span className="gradient-pink-text">{t.hero.title.split(" ").slice(-3).join(" ")}</span>
          </h1>

          <p className="mt-7 text-lg text-slate-600 leading-relaxed max-w-xl" data-testid="hero-subtitle">
            {t.hero.subtitle}
          </p>

          <div className="mt-9 flex flex-col sm:flex-row gap-4">
            <Link to="/#calculator" className="btn-pink" data-testid="hero-cta-primary">
              {t.hero.ctaPrimary} <ArrowRight size={18} />
            </Link>
            <Link to="/contact" className="btn-ghost" data-testid="hero-cta-secondary">
              {t.hero.ctaSecondary}
            </Link>
          </div>

          <div className="mt-12">
            <GoogleReviewsBadge />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="lg:col-span-7 relative z-10"
        >
          <div className="relative rounded-3xl overflow-hidden shadow-[0_30px_80px_rgba(15,23,42,0.18)] aspect-[4/3] lg:aspect-[3/2]">
            <img
              src={HERO_IMG}
              alt="Vrouw bij voordeur met fiets, BYD thuisbatterij zichtbaar"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-navy-900/30 via-transparent to-transparent" />

            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="absolute bottom-5 left-5 right-5 sm:bottom-7 sm:right-7 sm:left-auto glass rounded-2xl p-4 sm:p-5 sm:max-w-xs"
            >
              <div className="flex items-center gap-3">
                <img src={BATTERY_IMG} alt="Premium thuisbatterij" className="w-14 h-14 object-contain rounded-xl bg-white" />
                <div>
                  <div className="text-xs uppercase tracking-widest text-brand font-bold">Live</div>
                  <div className="font-heading font-bold text-navy-900">98% eigen energie</div>
                </div>
              </div>
              <div className="mt-3 h-2 bg-slate-100 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: "98%" }}
                  transition={{ delay: 0.9, duration: 1.2 }}
                  className="h-full gradient-pink"
                />
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
